import { Checkbox } from '@/components/ui/checkbox';
import { AGENT_ACCESS_COPY } from './agent-access-copy';

type SelectAllStateParams = { selected: number; total: number };

/** Radix's value for the box: ticked when all are, a dash when only some are. */
export const selectAllState = ({ selected, total }: SelectAllStateParams): boolean | 'indeterminate' => {
  if (total > 0 && selected >= total) return true;
  return selected > 0 ? 'indeterminate' : false;
};

type WebsiteSelectAllProps = SelectAllStateParams & { onToggle: () => void };

/**
 * The row above a website picker's list: tick every website in it, and how many are ticked. Ticking
 * it stores those websites as an explicit list, so a website added later is not included. It sits
 * outside the list's scroll, so it stays in view however long the list is.
 */
export const WebsiteSelectAll = ({ selected, total, onToggle }: WebsiteSelectAllProps) => (
  <div className="border-line flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
    <label className="flex cursor-pointer items-center gap-2.5">
      <Checkbox checked={selectAllState({ selected, total })} onCheckedChange={onToggle} disabled={total === 0} />
      <span className="text-foreground text-body">{AGENT_ACCESS_COPY.selectAll}</span>
    </label>
    <span className="supporting">{AGENT_ACCESS_COPY.selectedCount(selected, total)}</span>
  </div>
);

type WebsiteRowProps = { url: string; checked: boolean; onToggle: () => void };

/**
 * One website in a picker's list, drawn as `Checkbox` is, from the same tokens, but a native input,
 * because the list can run to thousands. Measured at 10,000 websites: with `Checkbox` each keystroke
 * in the form took over 6 seconds and the dialog 14 to open; with this, under half a second and 2.
 */
export const WebsiteRow = ({ url, checked, onToggle }: WebsiteRowProps) => (
  <label className="relative flex cursor-pointer items-center gap-2.5">
    <input type="checkbox" checked={checked} onChange={onToggle} className="peer sr-only" />
    <span
      aria-hidden
      className="border-primary peer-checked:bg-primary text-primary-foreground ring-offset-background peer-focus-visible:ring-ring h-4 w-4 shrink-0 rounded-sm border peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 peer-checked:[&>svg]:block"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="hidden h-full w-full">
        <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
    <span className="text-foreground truncate text-body" title={url}>
      {url}
    </span>
  </label>
);
